import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Trash2, ShieldCheck, UserPlus, Mail, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, AppRole } from '@/crm/auth/AuthContext';
import { translateError } from '@/crm/utils/translateError';
import { useConfirm } from '@/crm/components/ConfirmDialog';

const ROLES: AppRole[] = ['admin', 'direction', 'commercial', 'production', 'administratif', 'lecture'];

const ROLE_COLORS: Record<AppRole, string> = {
  admin: 'bg-red-500/15 text-red-400 border-red-500/30',
  direction: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  commercial: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  production: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  administratif: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  lecture: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

interface UserRow {
  user_id: string;
  email: string | null;
  display_name: string | null;
  roles: AppRole[];
  created_at: string;
}

export default function Utilisateurs() {
  const { hasRole, user: currentUser, refreshRoles } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingRole, setPendingRole] = useState<Record<string, AppRole>>({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<AppRole>('commercial');
  const [inviting, setInviting] = useState(false);

  const isAdmin = hasRole('admin');

  const sendInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    const { data, error } = await supabase.functions.invoke('invite-user', {
      body: { email: inviteEmail, full_name: inviteName, role: inviteRole },
    });
    setInviting(false);
    if (error || (data as any)?.error) {
      toast.error(translateError((data as any)?.error || error?.message) || 'Erreur lors de l\'invitation');
      return;
    }
    toast.success(`Invitation envoyée à ${inviteEmail}`);
    setInviteEmail(''); setInviteName(''); setInviteRole('commercial'); setInviteOpen(false);
    load();
  };

  const load = async () => {
    setLoading(true);
    // Requêtes en parallèle (au lieu de séquentielles)
    const [{ data: profiles }, { data: roles }] = await Promise.all([
      supabase.from('profiles').select('user_id, email, display_name, created_at').order('created_at', { ascending: false }),
      supabase.from('user_roles').select('user_id, role'),
    ]);

    const map = new Map<string, UserRow>();
    (profiles || []).forEach((p: any) => {
      map.set(p.user_id, { ...p, roles: [] });
    });
    (roles || []).forEach((r: any) => {
      const u = map.get(r.user_id);
      if (u) u.roles.push(r.role);
    });
    setUsers(Array.from(map.values()));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addRole = async (userId: string) => {
    const role = pendingRole[userId];
    if (!role) return;
    const { error } = await supabase.from('user_roles').insert({ user_id: userId, role });
    if (error) toast.error(translateError(error.message));
    else {
      toast.success(`Rôle « ${role} » attribué`);
      if (userId === currentUser?.id) await refreshRoles();
      load();
    }
  };

  const removeRole = async (userId: string, role: AppRole) => {
    if (userId === currentUser?.id && role === 'admin') {
      const ok = await confirm({
        title: 'Retirer votre rôle admin ?',
        description: 'Vous êtes sur le point de retirer votre propre rôle admin. Continuer ?',
        confirmLabel: 'Retirer',
        destructive: true,
      });
      if (!ok) return;
    }
    const { error } = await supabase.from('user_roles').delete().eq('user_id', userId).eq('role', role);
    if (error) toast.error(translateError(error.message));
    else {
      toast.success('Rôle retiré');
      if (userId === currentUser?.id) await refreshRoles();
      load();
    }
  };

  const deleteUser = async (userId: string, email: string | null) => {
    if (userId === currentUser?.id) {
      toast.error('Vous ne pouvez pas supprimer votre propre compte.');
      return;
    }
    const ok = await confirm({
      title: 'Supprimer cet utilisateur ?',
      description: `${email || userId}\n\nCette action est irréversible.`,
      confirmLabel: 'Supprimer définitivement',
      destructive: true,
    });
    if (!ok) return;
    const { data, error } = await supabase.functions.invoke('delete-user', { body: { user_id: userId } });
    if (error || (data as any)?.error) {
      toast.error(translateError((data as any)?.error || error?.message) || 'Erreur lors de la suppression');
      return;
    }
    toast.success('Utilisateur supprimé');
    load();
  };

  if (!isAdmin) {
    return (
      <Card className="p-8 bg-slate-900 border-slate-800 text-center">
        <ShieldCheck className="mx-auto text-amber-400 mb-3" size={28} />
        <h2 className="text-lg font-serif text-slate-100 mb-1">Accès réservé aux administrateurs</h2>
        <p className="text-sm text-slate-400">Seuls les comptes admin peuvent gérer les utilisateurs et les rôles.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif text-slate-100 flex items-center gap-2">
            <UserPlus className="text-amber-400" size={22} /> Utilisateurs & Rôles
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Invitez vos collaborateurs par email. L'inscription publique est désactivée — seul un admin peut créer un compte.
          </p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
              <Mail size={14} /> Inviter un utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
            <DialogHeader>
              <DialogTitle>Inviter un nouvel utilisateur</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="prenom@cygnature-production.com" className="bg-slate-950 border-slate-800" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Nom complet (optionnel)</Label>
                <Input type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)} className="bg-slate-950 border-slate-800" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Rôle</Label>
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as AppRole)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-slate-500">
                L'utilisateur recevra un email avec un lien pour définir son mot de passe et accéder au CRM.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteOpen(false)} className="bg-slate-950 border-slate-800">Annuler</Button>
              <Button onClick={sendInvite} disabled={inviting || !inviteEmail} className="bg-amber-500 hover:bg-amber-400 text-slate-950">
                {inviting ? 'Envoi…' : 'Envoyer l\'invitation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>


      {loading ? (
        <p className="text-slate-400 text-sm">Chargement…</p>
      ) : users.length === 0 ? (
        <Card className="p-6 bg-slate-900 border-slate-800 text-slate-400 text-sm">
          Aucun utilisateur inscrit pour le moment.
        </Card>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <Card key={u.user_id} className="p-4 bg-slate-900 border-slate-800">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-slate-100 font-medium">{u.display_name || u.email}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                  <div className="text-[10px] text-slate-600 mt-1">
                    Inscrit le {new Date(u.created_at).toLocaleDateString('fr-FR')}
                    {u.user_id === currentUser?.id && <span className="ml-2 text-amber-400">(vous)</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {u.roles.length === 0 ? (
                    <Badge variant="outline" className="border-red-500/30 text-red-400 bg-red-500/10">Aucun rôle</Badge>
                  ) : (
                    u.roles.map((r) => (
                      <Badge key={r} variant="outline" className={`${ROLE_COLORS[r]} gap-1`}>
                        {r}
                        <button
                          onClick={() => removeRole(u.user_id, r)}
                          className="ml-1 hover:text-red-400"
                          aria-label={`Retirer ${r}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800">
                <Select
                  value={pendingRole[u.user_id] || ''}
                  onValueChange={(v) => setPendingRole({ ...pendingRole, [u.user_id]: v as AppRole })}
                >
                  <SelectTrigger className="w-48 bg-slate-950 border-slate-800 text-slate-100">
                    <SelectValue placeholder="Choisir un rôle…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.filter((r) => !u.roles.includes(r)).map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => addRole(u.user_id)}
                  disabled={!pendingRole[u.user_id]}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950"
                >
                  Attribuer
                </Button>
                {u.user_id !== currentUser?.id && (
                  <Button
                    onClick={() => deleteUser(u.user_id, u.email)}
                    size="sm"
                    variant="outline"
                    className="ml-auto bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300 gap-1.5"
                  >
                    <UserX size={14} /> Supprimer l'utilisateur
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
